import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { Fit } from '../../../brunswick/constants'
import { QuickBuyContainer } from '../../../brunswick/containers/QuickBuyContainer'
import { EmbroideryConfig } from '../../../brunswick/containers/QuickBuyContainer/types'
import { useFetchFeaturedProductData } from '../../../lib/hooks/useFetchFeaturedProductData/useFetchFeaturedProductData'
import { useProductGroupDetails } from '../../../lib/hooks/useProductGroupDetails'
import { useVariantAttributeSelections } from '../../../lib/hooks/useVariantAttributeSelections'
import { useProductHref } from '../../../lib/routing'
import { AvailableProducts, areFitsEquivalent, resizeShopifyImage } from '../../../lib/utils'
import { useColorGroupsWithUnavailable } from '../useColorGroupsWithUnavailable'

import { useAddToCartWithMiniCartToggle } from './useAddToCartWithMiniCartToggle'
import { useCtaButton } from './useCtaButton'
import { useVariantValidator } from './useVariantValidator'

import type { IUseSelectVariant } from './types'
import type {
  PriceRange,
  Product,
  ProductAsset,
  ProductColor,
  ProductGroup,
  Variant,
} from '../../../types/graphql'

const defaultImageSize = 160

export function useSelectVariant({
  availableProducts,
  coloredAndFitVariants,
  embroideryConfig,
  imageSize = defaultImageSize,
  initialFeaturedProduct,
  initialSelectedColor,
  onCompleteAddToCart = noOp,
  onCompleteViewProduct = noOp,
  productCategory,
  productGroup,
  shouldResetSelections = false,
  initializeWithDefault = false,
  swPromoEligible,
  quantity,
  openMiniCartOnAdd = true,
}: {
  availableProducts?: AvailableProducts
  coloredAndFitVariants: Variant[]
  embroideryConfig?: EmbroideryConfig
  imageSize?: number
  initialFeaturedProduct: Product | undefined
  initialSelectedColor: ProductColor | undefined
  onCompleteAddToCart?(): void
  onCompleteViewProduct?(): void
  productCategory?: string
  productGroup: ProductGroup
  shouldResetSelections?: boolean
  initializeWithDefault?: boolean
  swPromoEligible?: boolean
  quantity?: number
  openMiniCartOnAdd?: boolean
}): IUseSelectVariant {
  const { selectAndAddVariant: strings } = useTranslation()
  const router = useRouter()

  const { addToCart } = QuickBuyContainer.useContainer()

  const {
    selectedColor,
    selectedFitKey,
    selectedSize,
    setSelectedColor,
    setSelectedFitKey,
    setSelectedSize,
  } = useVariantAttributeSelections({
    initialSelectedColor,
  })

  const [isEmbroideryVisible, setEmbroideryVisible] = useState(false)

  const selectedColorName = selectedColor?.name
  const useInitialFeaturedProduct =
    initialFeaturedProduct?.color == selectedColorName &&
    areFitsEquivalent(selectedFitKey ?? '', initialFeaturedProduct?.rawFit)

  const { featuredProduct } = useFetchFeaturedProductData({
    color: selectedColorName,
    fit: selectedFitKey || Fit.REGULAR,
    productGroupHandle: productGroup?.handle,
    skip: useInitialFeaturedProduct,
  })

  const activeProduct: Product | undefined = useInitialFeaturedProduct
    ? initialFeaturedProduct
    : featuredProduct ?? initialFeaturedProduct

  const {
    availableFitsKeys,
    coloredVariant,
    currentFitKey,
    currentSize,
    fitNames,
    selectedVariant,
    sizes,
  } = useProductGroupDetails({
    availableProducts: availableProducts ?? {},
    coloredAndFitVariants: activeProduct?.variants ?? coloredAndFitVariants,
    currentColorKey: selectedColor?.rawName,
    defaultVariantFitKey: productGroup?.defaultVariant?.rawFit ?? Fit.REGULAR,
    productGroup,
    productGroupFitsKeys: productGroup.rawFits,
    selectedFitKey,
    selectedSizeName: selectedSize?.shorthand,
  })

  const priceRange: PriceRange | undefined = activeProduct?.priceRange

  const galleryImages: readonly ProductAsset[] = activeProduct?.images?.nodes ?? []

  const image: ProductAsset | undefined = galleryImages[0]

  const imageSource: string | undefined =
    image &&
    resizeShopifyImage({
      height: imageSize,
      image,
      width: imageSize,
    }).source

  const colorGroups = productGroup?.colorGroups ?? []
  const { colorsGroupsWithUnavailable, isCurrentColorCompletelySoldOut } =
    useColorGroupsWithUnavailable({
      availableProducts: availableProducts || {},
      colorGroups,
      colorName: selectedColor?.rawName,
      currentFitKey,
      sizeName: currentSize?.shorthand,
    })

  const category = productCategory ?? coloredAndFitVariants[0]!.category

  const isActiveProductWaitlistable = !!activeProduct?.waitlistable

  const validation = useVariantValidator({ selectedVariant })

  const { addToCart: addToCartWithMiniCartToggle } = useAddToCartWithMiniCartToggle()

  const addToCartWithValidation = useCallback(
    (params: Omit<Parameters<typeof addToCartWithMiniCartToggle>[0], 'finalSale' | 'variant'>) => {
      if (!validation.validate() || !selectedVariant) return
      addToCartWithMiniCartToggle({
        ...params,
        finalSale: activeProduct?.finalSale,
        swPromoEligible,
        variant: selectedVariant,
        openMiniCartOnAdd,
        ...(quantity != null && { quantity }),
      })

      onCompleteAddToCart()
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeProduct,
      addToCartWithMiniCartToggle,
      onCompleteAddToCart,
      addToCart,
      selectedVariant,
      validation.validate,
      quantity,
    ]
  )

  const addToCartWithEmbroidery = useCallback(
    (selected: boolean) => {
      const embroideryVariant = selected ? embroideryConfig?.variant : undefined

      return addToCartWithValidation({ embroideryConfig, embroideryVariant })
    },
    [addToCartWithValidation, embroideryConfig]
  )

  useEffect(() => {
    if (initializeWithDefault) {
      setSelectedFitKey(availableFitsKeys[0])
      setSelectedSize(sizes.find(size => !size.soldOut))
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeWithDefault])

  useEffect(() => {
    validation.reset()
    setEmbroideryVisible(false)
    if (shouldResetSelections) {
      setSelectedSize(undefined)
      setSelectedFitKey(undefined)
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldResetSelections])

  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(validation.reset, [selectedColor, selectedFitKey, selectedSize])

  const sizeSectionTitle: string = validation.invalid
    ? strings.sizeSection.titleWhenInvalid
    : strings.sizeSection.title

  const onSelectEmbroidery = useCallback(() => {
    if (!validation.validate()) return
    setEmbroideryVisible(true)
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.validate, setEmbroideryVisible])

  const onAddToCart = useCallback(() => addToCartWithValidation({}), [addToCartWithValidation])

  const productHref = useProductHref(productGroup.handle, {
    color: selectedColorName,
    size: currentSize?.shorthand,
  })
  const onViewProduct = useCallback(() => {
    router.push(productHref)
    onCompleteViewProduct()
  }, [router, productHref, onCompleteViewProduct])

  const hasSize = !!currentSize
  const soldOut = isCurrentColorCompletelySoldOut || !!currentSize?.soldOut
  const invalid = validation.invalid

  const ctaButton = useCtaButton({
    hasEmbroidery: !!embroideryConfig,
    hasSize,
    invalid,
    onAddToCart,
    onSelectEmbroidery,
    onViewProduct,
    soldOut,
    waitlistable: isActiveProductWaitlistable,
  })

  const productGroupTitle: string = `${productGroup.style} ${productGroup.description}`
  const productHandle = activeProduct?.handle || ''
  const productShopifyId = activeProduct?.shopifyId || ''
  const productId = activeProduct?.id || ''

  const isValidToAdd = hasSize && !invalid && !soldOut

  return {
    addToCartWithEmbroidery,
    availableFitsKeys,
    category,
    ctaButton,
    currency: coloredVariant?.priceDetails.price.currency,
    currentFitKey,
    currentSize,
    discountPrice: coloredVariant?.priceDetails.discountPrice?.amount,
    discountPriceExplicitFmt: coloredVariant?.discountPriceExplicitFmt,
    embroideryConfig,
    fitNames,
    imageSource,
    isActiveProductWaitlistable,
    isCurrentColorCompletelySoldOut,
    isEmbroideryVisible,
    isValidToAdd,
    price: coloredVariant?.priceDetails.price.amount,
    priceExplicitFmt: coloredVariant?.priceExplicitFmt,
    priceRange,
    productGroupTitle,
    productHandle,
    productShopifyId,
    productId,
    selectedColor,
    setSelectedColor,
    setSelectedFitKey,
    setSelectedSize,
    sizes,
    colorGroups: colorsGroupsWithUnavailable,
    sizeSectionTitle,
    /**
     * userHasAttemptedInvalidAddToBag is a victim of prop drilling in some
     * of the underlying components so let's not rename it just yet.
     * Author: Botond 2021-10-22
     */
    userHasAttemptedInvalidAddToBag: validation.invalid,
  }
}

function noOp() {}
